import moment from 'moment';
import { isNil } from 'lodash';
import { objectType } from 'types';
import { UTC_OFFSET } from './hooks/useCountDown';

export const parseSecondsToMinutes = (secondsInput: number) => {
  const duration = moment.duration(secondsInput, 'seconds');

  // Use asMinutes function to get the total minutes, which might be a floating point number
  const minutes = Math.floor(duration.asMinutes());
  const seconds = secondsInput - minutes * 60;

  return {
    minutes,
    seconds,
  };
};

export const parseSecondsToMinutesAndHours = (secondsInput: number) => {
  // const duration = moment.duration(secondsInput, 'seconds');

  // Use asMinutes function to get the total minutes, which might be a floating point number
  const duration = moment.duration(secondsInput, 'seconds');

  const hours = Math.floor(duration.asHours());
  const minutes = Math.floor(duration.minutes());
  const seconds = Math.floor(duration.seconds());

  return {
    minutes,
    seconds,
    hours,
  };
};

export const formatDate = (
  date: moment.Moment,
  locale: 'vi' | 'en',
  format: (dayOfWeek: string, date: moment.Moment) => string,
) => {
  const DAYS_OF_WEEK_VI = [
    'Chủ Nhật',
    'Thứ 2',
    'Thứ 3',
    'Thứ 4',
    'Thứ 5',
    'Thứ 6',
    'Thứ 7',
  ];
  const DAYS_OF_WEEK_EN = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];
  const dayOfWeek =
    locale === 'vi' ? DAYS_OF_WEEK_VI[date.day()] : DAYS_OF_WEEK_EN[date.day()];
  // Format the rest of the date
  return format(dayOfWeek, date);
};

export const formatShortBracketDate = (
  dateStr?: string,
  locale: 'vi' | 'en' = 'vi',
) => {
  if (!dateStr) return '';
  const m = moment(dateStr, ['DD/MM/YYYY', moment.ISO_8601], true);
  const mm = m.isValid() ? m : moment(dateStr, 'DD/MM/YYYY');
  if (!mm.isValid()) return dateStr;

  if (locale === 'vi') {
    const day = mm.day(); // 0 = Sun
    const dayLabel = day === 0 ? 'CN' : `T${day + 1}`;
    return `${dayLabel}, ${mm.format('DD/MM/YYYY')}`;
  }

  return `${mm.format('ddd')}, ${mm.format('MM/DD/YYYY')}`;
};

export const parseTimeRange = (rangeStr?: string) => {
  if (!rangeStr) return undefined;
  const [min, max] = rangeStr.split('-').map(Number);
  return {
    from: `${String(min).padStart(2, '0')}:00:00`,
    to: `${String(max).padStart(2, '0')}:00:00`,
  };
};

export const getDateStringForValidDate = (
  fromDate: string,
  toDate: string,
  numberDateAddToFrom?: number,
  formatFunc?: (fromDate: moment.Moment, toDate: moment.Moment) => string,
) => {
  const standardDayAdd = isNil(numberDateAddToFrom) ? 0 : numberDateAddToFrom;
  const addDay = standardDayAdd <= 0 ? 1 : standardDayAdd;
  const fromDateMoment = moment(fromDate, 'DD/MM/YYYY');
  const toDateByAddDayMoment = moment(fromDate, 'DD/MM/YYYY').add(
    addDay - 1,
    'days',
  );
  // toDateByAddDayMoment.add(addDay - 1, 'days');
  const toDateMoment = moment(toDate, 'DD/MM/YYYY');
  const standardToDateMoment = toDateByAddDayMoment.isBefore(toDateMoment)
    ? toDateByAddDayMoment
    : toDateMoment;

  if (
    fromDateMoment.format('DD/MM/YYYY') ===
    standardToDateMoment.format('DD/MM/YYYY')
  ) {
    return formatFunc
      ? formatFunc(fromDateMoment, fromDateMoment)
      : fromDateMoment.format('DD/MM/YYYY');
  }
  return formatFunc
    ? formatFunc(fromDateMoment, standardToDateMoment)
    : `${fromDateMoment.format('DD/MM/YYYY')} - ${standardToDateMoment.format(
        'DD/MM/YYYY',
      )}`;
};

class BaseTimer {
  protected worker: Worker | null;
  protected listeners: (() => void)[];

  constructor() {
    this.worker = null;
    this.listeners = [];
  }

  start(interval: number, onTick: () => void) {
    if (this.worker) {
      return; // Timer is already running
    }
    // Create a new Worker
    this.worker = new Worker(
      URL.createObjectURL(
        new Blob(
          [
            `
    setInterval(() => {
      postMessage('tick');
    }, ${interval});
    `,
          ],
          { type: 'application/javascript' },
        ),
      ),
    );

    // Listen for messages from the Worker
    this.worker.onmessage = event => {
      onTick();
    };
  }

  stop() {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
  }

  addListener(listener: () => void) {
    this.listeners.push(listener);
  }

  removeListener(listener: () => void) {
    const index = this.listeners.indexOf(listener);
    if (index !== -1) {
      this.listeners.splice(index, 1);
    }
  }

  protected fireEvent() {
    this.listeners.forEach(listener => listener());
  }
}

export class CountdownTimer extends BaseTimer {
  private countdown: number;

  constructor(countdown: number) {
    super();
    this.countdown = countdown;
  }

  start(interval: number, onTick?: (time: number) => void) {
    super.start(1000, () => {
      this.countdown--;
      onTick && onTick(this.countdown);
      if (this.countdown <= 0) {
        this.stop();
        this.fireEvent();
      }
    });
  }

  reset() {
    this.stop();
    this.countdown = 0;
  }

  resetTo(countdown: number) {
    this.stop();
    this.countdown = countdown;
  }
}

export class IntervalTimer extends BaseTimer {
  private timeInterval: number;
  constructor(timeInterval: number) {
    super();
    this.timeInterval = timeInterval;
  }
  start() {
    super.start(this.timeInterval, () => {
      this.fireEvent();
    });
  }

  reset() {
    this.stop();
    this.start();
  }
}

export const isTimeValid = (hourFrom: any, hourTo: any) => {
  const parseHourFrom = moment(hourFrom, 'HH:mm').format('HH');
  const parseHourTo = moment(hourTo, 'HH:mm').format('HH');
  const parseMinuteFrom = moment(hourFrom, 'HH:mm').format('mm');
  const parseMinuteTo = moment(hourTo, 'HH:mm').format('mm');
  const currentDate = moment().utcOffset(UTC_OFFSET);
  const currentHour = currentDate.hours() + currentDate.minutes() / 60;
  const isValidHour =
    currentHour >=
      parseInt(parseHourFrom, 10) + parseInt(parseMinuteFrom, 10) / 60 &&
    currentHour < parseInt(parseHourTo, 10) + parseInt(parseMinuteTo, 10) / 60;
  return isValidHour;
};

export const getDayOfWeekString = (
  date: moment.Moment,
  translation: objectType | undefined,
) => {
  const DAYS_OF_WEEK = [
    translation?.['b2c.txt_day_sunday'] || 'Chủ Nhật',
    translation?.['b2c.txt_day_monday'] || 'Thứ 2',
    translation?.['b2c.txt_day_tuesday'] || 'Thứ 3',
    translation?.['b2c.txt_day_wednesday'] || 'Thứ 4',
    translation?.['b2c.txt_day_thursday'] || 'Thứ 5',
    translation?.['b2c.txt_day_friday'] || 'Thứ 6',
    translation?.['b2c.txt_day_saturday'] || 'Thứ 7',
  ];
  const dayOfWeek = DAYS_OF_WEEK[date.day()];
  return dayOfWeek;
};
